import { motion } from 'framer-motion';
import { type ChangeEvent, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Sparkles } from '@/components/icons';
import { MetricCard, PageSection } from '@/components/system';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { containerVariants, itemVariants } from '@/pages/dashboard/animations';
import {
    formatMetricValue,
    getMetricLabel,
    resolveScoreConversion,
    type ScoreConversion,
    type ScoreConversionResult,
    type ScoreMetric,
} from '../utils';
import MobileNormalDistributionChart from './MobileNormalDistributionChart';
import NormalDistributionChart from './NormalDistributionChart';

const METRIC_OPTIONS: Array<{
    metric: ScoreMetric;
    label: string;
    hint: string;
    placeholder: string;
}> = [
    {
        metric: 'z',
        label: 'Escore Z',
        hint: 'Escala padrão centrada em 0, com desvio padrão igual a 1.',
        placeholder: 'Ex.: 1,25',
    },
    {
        metric: 't',
        label: 'Escore T',
        hint: 'Escala linear com média 50 e desvio padrão 10.',
        placeholder: 'Ex.: 62,50',
    },
    {
        metric: 'percentile',
        label: 'Percentil',
        hint: 'Valor cumulativo entre 0 e 100 para localizar o ponto na curva.',
        placeholder: 'Ex.: 89,00',
    },
];

const REFERENCE_FORMULAS = [
    {
        title: 'Conversão linear para T',
        formula: 'T = 50 + 10z',
        description: 'Cada desvio padrão no eixo Z desloca 10 pontos na escala T.',
    },
    {
        title: 'Percentil acumulado',
        formula: 'Percentil = Phi(z) x 100',
        description: 'A função Phi representa a área acumulada sob a curva normal padrão.',
    },
    {
        title: 'Retorno de T para Z',
        formula: 'z = (T - 50) / 10',
        description: 'A ferramenta usa essa inversão para sincronizar os três campos.',
    },
    {
        title: 'Retorno de percentil para Z',
        formula: 'z = Phi^-1(percentil / 100)',
        description:
            'Percentis exatos 0 e 100 não são invertíveis porque apontam para os extremos teóricos da curva.',
    },
];

const MOBILE_METRIC_TABS: Array<{
    metric: ScoreMetric;
    label: string;
    shortLabel: string;
}> = [
    { metric: 'z', label: 'Escore Z', shortLabel: 'Z' },
    { metric: 't', label: 'Escore T', shortLabel: 'T' },
    { metric: 'percentile', label: 'Percentil', shortLabel: '%' },
];

function MobileToolsCalculator({
    selectedMetric,
    selectedOption,
    draftValue,
    conversion,
    conversionResult,
    onMetricChange,
    onDraftValueChange,
    onZChange,
    onOpenReference,
}: {
    selectedMetric: ScoreMetric;
    selectedOption: (typeof METRIC_OPTIONS)[number];
    draftValue: string;
    conversion: ScoreConversion | null;
    conversionResult: ScoreConversionResult;
    onMetricChange: (metric: ScoreMetric) => void;
    onDraftValueChange: (value: string) => void;
    onZChange: (z: number) => void;
    onOpenReference: () => void;
}) {
    const resultItems: Array<{
        metric: ScoreMetric;
        label: string;
    }> = [
        { metric: 'z', label: 'Z' },
        { metric: 't', label: 'T' },
        { metric: 'percentile', label: 'Percentil' },
    ];

    const handleCopyResults = async () => {
        if (!conversion) {
            return;
        }

        const content = resultItems
            .map(
                (item) =>
                    `${getMetricLabel(item.metric)}: ${formatMetricValue(
                        item.metric,
                        conversion[item.metric],
                    )}`,
            )
            .join('\n');

        try {
            await navigator.clipboard.writeText(content);
            toast.success('Valores copiados.');
        } catch {
            toast.error('Não foi possível copiar os valores.');
        }
    };

    return (
        <div className="space-y-4 md:hidden">
            <section className="rounded-4xl border border-border bg-surface p-4 shadow-elevation-1">
                <div
                    role="radiogroup"
                    aria-label="Escala de entrada"
                    className="grid grid-cols-3 gap-1 rounded-3xl bg-brand-subtle p-1"
                >
                    {MOBILE_METRIC_TABS.map((tab) => {
                        const isActive = selectedMetric === tab.metric;

                        return (
                            <button
                                key={tab.metric}
                                type="button"
                                role="radio"
                                aria-checked={isActive}
                                className={cn(
                                    'flex min-h-14 flex-col items-center justify-center rounded-2xl px-2 text-center transition-all',
                                    isActive
                                        ? 'bg-surface text-brand shadow-sm'
                                        : 'text-muted-foreground',
                                )}
                                onClick={() => onMetricChange(tab.metric)}
                            >
                                <span className="text-base font-bold leading-none">
                                    {tab.shortLabel}
                                </span>
                                <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.08em]">
                                    {tab.label}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="mt-5 space-y-2">
                    <Label
                        htmlFor="tools-mobile-input-value"
                        className="text-sm font-semibold text-foreground"
                    >
                        Valor informado
                    </Label>
                    <Input
                        id="tools-mobile-input-value"
                        type="text"
                        inputMode="decimal"
                        placeholder={selectedOption.placeholder}
                        value={draftValue}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            onDraftValueChange(event.target.value)
                        }
                        className="h-14 rounded-2xl border-border bg-background text-2xl font-semibold tabular-nums shadow-none"
                    />
                    <p className="text-xs leading-relaxed text-muted-foreground">
                        {selectedOption.hint}
                    </p>
                </div>
            </section>

            {conversionResult.error && (
                <Alert variant="warning" icon={<Sparkles className="size-4" />}>
                    <span>{conversionResult.error}</span>
                </Alert>
            )}

            <section className="rounded-4xl border border-border bg-surface p-4 shadow-elevation-1">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h2 className="text-base font-semibold tracking-tight text-foreground">
                            Conversões
                        </h2>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Resultado pronto para consulta rápida.
                        </p>
                    </div>
                    <Button
                        type="button"
                        variant="surface"
                        size="sm"
                        className="h-9 rounded-2xl px-3"
                        disabled={!conversion}
                        onClick={handleCopyResults}
                    >
                        <Copy className="size-3.5" />
                        Copiar
                    </Button>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2">
                    {resultItems.map((item) => (
                        <div
                            key={item.metric}
                            className={cn(
                                'rounded-2xl border border-border bg-background p-3',
                                selectedMetric === item.metric && 'border-brand/15 bg-brand-subtle',
                            )}
                        >
                            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                                {item.label}
                            </p>
                            <p className="mt-2 text-lg font-semibold leading-none tracking-tight text-foreground">
                                {conversion
                                    ? formatMetricValue(item.metric, conversion[item.metric])
                                    : '-'}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            <MobileNormalDistributionChart conversion={conversion} onZChange={onZChange} />

            <Button
                type="button"
                variant="tint"
                className="h-12 w-full rounded-2xl"
                onClick={onOpenReference}
            >
                Abrir referência rápida
            </Button>
        </div>
    );
}

export default function ScoreConversionCalculator() {
    const [isReferenceOpen, setIsReferenceOpen] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState<ScoreMetric>('z');
    const [draftValue, setDraftValue] = useState('0,85');

    const conversionResult = useMemo(
        () => resolveScoreConversion(selectedMetric, draftValue),
        [selectedMetric, draftValue],
    );

    const conversion = conversionResult.data;
    const selectedOption =
        METRIC_OPTIONS.find((option) => option.metric === selectedMetric) ?? METRIC_OPTIONS[0];

    const handleMetricChange = (metricValue: string) => {
        const metric = metricValue as ScoreMetric;

        setSelectedMetric(metric);
        setDraftValue(conversion ? formatMetricValue(metric, conversion[metric]) : '');
    };

    const handleZChange = (nextZ: number) => {
        setSelectedMetric('z');
        setDraftValue(formatMetricValue('z', nextZ));
    };

    return (
        <>
            <MobileToolsCalculator
                selectedMetric={selectedMetric}
                selectedOption={selectedOption}
                draftValue={draftValue}
                conversion={conversion}
                conversionResult={conversionResult}
                onMetricChange={handleMetricChange}
                onDraftValueChange={setDraftValue}
                onZChange={handleZChange}
                onOpenReference={() => setIsReferenceOpen(true)}
            />

            <div className="hidden md:block">
                <PageSection title="Conversor de escores" headerClassName="sm:items-center">
                    <div className="space-y-6">
                        <div className="rounded-4xl border border-border bg-surface p-5 shadow-elevation-1">
                            <div className="grid gap-4 lg:grid-cols-[240px,minmax(0,1fr)] lg:items-end">
                                <div className="space-y-2">
                                    <Label
                                        htmlFor="tools-metric-select"
                                        className="text-sm font-semibold text-foreground"
                                    >
                                        Escala de entrada
                                    </Label>
                                    <Select
                                        value={selectedMetric}
                                        onValueChange={handleMetricChange}
                                    >
                                        <SelectTrigger
                                            id="tools-metric-select"
                                            className="h-12 rounded-2xl border-border bg-surface text-base font-semibold shadow-none"
                                        >
                                            <SelectValue placeholder="Selecione a escala" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {METRIC_OPTIONS.map((option) => (
                                                <SelectItem
                                                    key={option.metric}
                                                    value={option.metric}
                                                >
                                                    {option.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label
                                        htmlFor="tools-input-value"
                                        className="text-sm font-semibold text-foreground"
                                    >
                                        Valor informado
                                    </Label>
                                    <Input
                                        id="tools-input-value"
                                        type="text"
                                        inputMode="decimal"
                                        placeholder={selectedOption.placeholder}
                                        value={draftValue}
                                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                            setDraftValue(event.target.value)
                                        }
                                        className="h-12 rounded-2xl border-border bg-surface text-lg font-semibold tabular-nums shadow-none"
                                    />
                                </div>
                            </div>

                            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                                {selectedOption.hint}
                            </p>
                        </div>

                        {conversionResult.error && (
                            <Alert variant="warning" icon={<Sparkles className="size-4" />}>
                                <span>{conversionResult.error}</span>
                            </Alert>
                        )}

                        <motion.div
                            className="grid gap-4 md:grid-cols-3"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            <motion.div variants={itemVariants}>
                                <MetricCard
                                    title="Escore Z"
                                    value={conversion ? formatMetricValue('z', conversion.z) : '-'}
                                    meta="Referência padronizada"
                                    appearance="minimal"
                                />
                            </motion.div>
                            <motion.div variants={itemVariants}>
                                <MetricCard
                                    title="Escore T"
                                    value={conversion ? formatMetricValue('t', conversion.t) : '-'}
                                    meta="Escala com média 50"
                                    appearance="minimal"
                                />
                            </motion.div>
                            <motion.div variants={itemVariants}>
                                <MetricCard
                                    title="Percentil"
                                    value={
                                        conversion
                                            ? formatMetricValue('percentile', conversion.percentile)
                                            : '-'
                                    }
                                    meta="Posição acumulada"
                                    appearance="minimal"
                                />
                            </motion.div>
                        </motion.div>

                        <NormalDistributionChart
                            conversion={conversion}
                            onZChange={handleZChange}
                            onOpenReference={() => setIsReferenceOpen(true)}
                        />
                    </div>
                </PageSection>
            </div>

            <Dialog open={isReferenceOpen} onOpenChange={setIsReferenceOpen}>
                <DialogContent className="bottom-0 top-auto max-h-[86dvh] max-w-none translate-y-0 gap-0 overflow-hidden rounded-t-4xl border-border p-0 shadow-elevation-2 sm:bottom-auto sm:top-[50%] sm:max-w-3xl sm:translate-y-[-50%] sm:rounded-lg">
                    <DialogHeader className="border-b border-border px-4 py-5 text-left sm:px-6">
                        <DialogTitle className="text-xl font-semibold tracking-tight text-foreground">
                            Referência rápida
                        </DialogTitle>
                        <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
                            Fórmulas e premissas usadas nas conversões para manter a leitura
                            estatística consistente.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="max-h-[calc(86dvh-112px)] overflow-y-auto px-4 py-5 sm:max-h-[70vh] sm:px-6">
                        <div className="grid gap-4 lg:grid-cols-2">
                            {REFERENCE_FORMULAS.map((item) => (
                                <div
                                    key={item.title}
                                    className="rounded-3xl border border-border bg-surface p-5"
                                >
                                    <p className="text-sm font-semibold text-foreground">
                                        {item.title}
                                    </p>
                                    <p className="mt-3 font-mono text-lg font-semibold text-brand">
                                        {item.formula}
                                    </p>
                                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                                        {item.description}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-5 rounded-3xl border border-brand/15 bg-brand-subtle px-5 py-4 text-sm leading-relaxed text-muted-foreground">
                            Este cálculo usa a distribuição normal padrão para converter percentil e
                            escores. Ele não substitui normas clínicas específicas nem a
                            interpretação qualitativa de cada instrumento.
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
